import {
  Button,
  Media,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@ameelio/ui";
import { useQuery } from "@apollo/client/react";
import CheckBoxOutlined from "@mui/icons-material/CheckBoxOutlined";
import DoNotDisturbAltOutlined from "@mui/icons-material/DoNotDisturbAltOutlined";
import { Box, Chip, Link as MUILink, Typography } from "@mui/material";
import { ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { ContentSkeleton } from "../lib/closet";
import DetailsStack, { DetailsStackItem } from "../lib/DetailsStack";
import featureTypeToHumanReadable from "../lib/featureTypeToHumanReadable";
import Link from "../lib/Link";
import Screen from "../lib/Screen";
import ScreenSection, { ScreenSectionTitle } from "../lib/ScreenSection";
import { useIsAmeelioSupport } from "../SessionBoundary";
import { GetSystemDocument, GetSystemQuery } from "./GetSystem.generated";
import UpdateFacilityCodeDialog from "./UpdateFacilityCodeDialog";
import UpdateFacilityFeaturesDialog from "./UpdateFacilityFeaturesDialog";
import UpdateFacilityTabletGroupsDialog from "./UpdateFacilityTabletGroupsDialog";

const tld = (import.meta.env.VITE_API_ORIGIN || "").endsWith("org")
  ? "org"
  : "xyz";

function residentPortalDomain(code: string) {
  return `${code.toLowerCase()}-app.ameelio.${tld}`;
}

// https://developers.cloudflare.com/1.1.1.1/encryption/dns-over-https/make-api-requests/dns-json/
async function checkDNS(domain: string) {
  const data = await (
    await fetch(`https://cloudflare-dns.com/dns-query?name=${domain}`, {
      headers: { Accept: "application/dns-json" },
    })
  ).json();
  return "Status" in data && data.Status === 0;
}

export default function SystemScreen() {
  const { systemId } = useParams<{ systemId: string }>();
  const { t } = useTranslation();
  if (!systemId) throw new Error("Missing system id");

  const support = useIsAmeelioSupport();

  const { data, error } = useQuery(GetSystemDocument, {
    variables: { systemId },
    fetchPolicy: "cache-and-network",
  });

  // async check to see if the domain is live yet. requires engineering.
  const [domainRegistered, setDomainRegistered] = useState(false);
  useEffect(() => {
    if (!data?.system.code) return;
    checkDNS(residentPortalDomain(data.system.code)).then(setDomainRegistered);
  }, [data?.system.code]);

  const [editFacility, setEditFacility] =
    useState<GetSystemQuery["system"]["facilities"][number]>();
  const [codeFacility, setCodeFacility] =
    useState<GetSystemQuery["system"]["facilities"][number]>();
  const [tabletGroupsFacilityId, setTabletGroupsFacilityId] =
    useState<string>();

  if (error) throw error;
  if (!data) return <ContentSkeleton />;

  const { system } = data;
  const facilities = system.facilities
    .slice(0)
    .sort((a, b) => a.publicId.localeCompare(b.publicId));

  return (
    <Screen
      headerAction={
        <Link
          button
          variant="outlined"
          to={`/toolbox/systems/${system.id}/edit`}
          sx={{ flexGrow: 1 }}
          disabled={!support}
        >
          Edit system
        </Link>
      }
      title={`${system.name} System`}
      maxWidth="md"
    >
      {editFacility && (
        <UpdateFacilityFeaturesDialog
          facility={editFacility}
          onClose={() => setEditFacility(undefined)}
        />
      )}
      {codeFacility && (
        <UpdateFacilityCodeDialog
          facility={codeFacility}
          onClose={() => setCodeFacility(undefined)}
        />
      )}
      {tabletGroupsFacilityId && (
        <UpdateFacilityTabletGroupsDialog
          facilityId={tabletGroupsFacilityId}
          onClose={() => setTabletGroupsFacilityId(undefined)}
        />
      )}
      <ScreenSection title="Details">
        <DetailsStack>
          <DetailsStackItem label="Roster URL">
            {import.meta.env.VITE_API_ORIGIN}/api/v1/systems/{system.id}/roster
          </DetailsStackItem>
          <DetailsStackItem label="Data retention">
            {system.dataRetention != null
              ? `${system.dataRetention} days`
              : t("No data retention.")}
          </DetailsStackItem>
          <DetailsStackItem label="Recording retention">
            {system.recordingRetention} days
          </DetailsStackItem>
          <DetailsStackItem label="Resident domain">
            <Media
              gap={0.5}
              image={
                domainRegistered ? (
                  <CheckBoxOutlined sx={{ height: 18 }} />
                ) : (
                  <Tooltip title="contact engineering to configure">
                    <DoNotDisturbAltOutlined sx={{ height: 18 }} />
                  </Tooltip>
                )
              }
            >
              <MUILink href={`https://${residentPortalDomain(system.code)}`}>
                https://{residentPortalDomain(system.code)}
              </MUILink>
            </Media>
          </DetailsStackItem>
          <DetailsStackItem label="Visitor reapproval">
            {`${system.visitorReapprovalDays} days`}
          </DetailsStackItem>
          <DetailsStackItem label="Visitor max password age">
            {system.maxPasswordAgeDays
              ? `${system.maxPasswordAgeDays} days`
              : "No limit"}
          </DetailsStackItem>
          <DetailsStackItem label="Call transcripts enabled?">
            {system.hasCallTranscripts ? "Yes" : "No"}
          </DetailsStackItem>
          <DetailsStackItem label="Tablet management enabled?">
            {system.hasTablets ? "Yes" : "No"}
          </DetailsStackItem>
          <DetailsStackItem label="Resident reset password flow enabled?">
            {system.allowInmatePasswordReset ? "Yes" : "No"}
          </DetailsStackItem>
          <DetailsStackItem label="Covert message flagging enabled?">
            {system.enableCovertMessageFlagging ? "Yes" : "No"}
          </DetailsStackItem>
        </DetailsStack>
      </ScreenSection>

      <section>
        <ScreenSectionTitle title="Facilities" />
        <Box
          sx={{
            mt: 1,
            mb: 2,
          }}
        >
          <Link
            button
            disabled={!support}
            variant="outlined"
            to={`/toolbox/systems/${systemId}/facilities/new`}
          >
            Add another
          </Link>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Code</TableCell>
                <TableCell>Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {facilities.map((f) => (
                <TableRow key={f.id}>
                  <TableCell>
                    {f.publicId}
                    <Button
                      variant="text"
                      size="small"
                      disabled={!support}
                      onClick={() => setCodeFacility(f)}
                      aria-label={`Edit code ${f.publicId}`}
                    >
                      Edit
                    </Button>
                  </TableCell>
                  <TableCell>
                    <FacilityDetailSection
                      title="Features"
                      isEmpty={f.features.length === 0}
                      onEdit={() => setEditFacility(f)}
                      disabled={!support}
                    >
                      {f.features
                        .map(featureTypeToHumanReadable)
                        .sort()
                        .map((feature) => (
                          <Chip key={feature} label={feature} sx={{ m: 0.5 }} />
                        ))}
                    </FacilityDetailSection>
                    {system.hasTablets && (
                      <FacilityDetailSection
                        title="Tablet groups"
                        isEmpty={f.tabletGroups.length === 0}
                        onEdit={() => setTabletGroupsFacilityId(f.id)}
                        disabled={!support}
                      >
                        {f.tabletGroups.map((g) => (
                          <Chip
                            key={`${g.mdm}:${g.externalId}`}
                            label={g.name}
                            sx={{ m: 0.5 }}
                          />
                        ))}
                      </FacilityDetailSection>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </section>
    </Screen>
  );
}

function FacilityDetailSection({
  title,
  isEmpty,
  onEdit,
  disabled,
  children,
}: {
  title: string;
  isEmpty: boolean;
  onEdit: () => void;
  disabled: boolean;
  children: ReactNode;
}) {
  return (
    <Box sx={{ "& + &": { mt: 1 } }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography variant="caption" color="text.secondary">
          {title}
        </Typography>
        <Button
          variant="text"
          size="small"
          disabled={disabled}
          onClick={onEdit}
          aria-label={`${isEmpty ? "Add" : "Edit"} ${title}`}
        >
          {isEmpty ? "Add" : "Edit"}
        </Button>
      </Box>
      <Box>
        {isEmpty ? (
          <Typography variant="body2" sx={{ m: 0.5 }}>
            None
          </Typography>
        ) : (
          children
        )}
      </Box>
    </Box>
  );
}
